import OpenInNew from "@mui/icons-material/OpenInNew";
import { Tooltip } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { MouseEvent } from "react";

function missingCodesLabel(missingFederalWasteCodes: string[]): string {
  return `Missing from the registration: ${missingFederalWasteCodes.join(", ")}`;
}

/**
 * The open registration submission's link, a pill matching the "Update
 * needed" chip it replaces once the submission exists. An update submission
 * also carries the missing-codes tooltip and accessible name that the
 * (now-suppressed) "Update needed" chip would show; a new-registration
 * submission only differs by label.
 */
export function OpenSubmissionChip({
  submissionId,
  isUpdate,
  onOpenSubmission,
  missingFederalWasteCodes,
}: {
  submissionId: string;
  isUpdate: boolean;
  onOpenSubmission: (submissionId: string) => void;
  missingFederalWasteCodes?: string[];
}) {
  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onOpenSubmission(submissionId);
  };

  const label =
    isUpdate && missingFederalWasteCodes?.length
      ? missingCodesLabel(missingFederalWasteCodes)
      : undefined;
  const chip = (
    <StatusChip
      color="info"
      variant="outlined"
      label={isUpdate ? "Update in progress" : "Submission"}
      icon={<OpenInNew fontSize="small" />}
      onClick={handleOpen}
      sx={{ flexShrink: 0 }}
    />
  );

  // describeChild, so the codes become the chip's accessible description rather
  // than replacing "Update in progress" as its name.
  return label ? (
    <Tooltip title={label} describeChild>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
}

/**
 * Flags a registration missing federal codes the stream now carries. Clickable
 * only where the caller can act on it (the grid creates the update submission;
 * the registration drawer just reports it).
 */
export function UpdateNeededChip({
  missingFederalWasteCodes,
  onClick,
}: {
  missingFederalWasteCodes: string[];
  onClick?: () => void;
}) {
  return (
    <Tooltip title={missingCodesLabel(missingFederalWasteCodes)} describeChild>
      <StatusChip
        color="warning"
        variant="outlined"
        label="Update needed"
        onClick={
          onClick
            ? (event: MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                onClick();
              }
            : undefined
        }
        sx={{ flexShrink: 0 }}
      />
    </Tooltip>
  );
}
